import CircleRoundedIcon from "@mui/icons-material/CircleRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import { Box, Divider, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import { inAppNotificationKeys } from "../../../api/queryKeys";
import { Toast } from "../../../components/Toast";
import SvgToIcon from "../../../icons/SvgToIcon";
import { NotificationType } from "../../../utils/constants";
import { timePassedSince } from "../../../utils/timestamp";

import {
  detectionIcons,
  icons,
  severities,
  sidebarIcons,
} from "../../stack/environment/icons/iconExport";
import { FooterEntity } from "./FooterEntity";
import { FooterGap } from "./FooterGap";
import { FooterScenario } from "./FooterScenario";
import CustomMenu from "./MoreInfoMenu";

const InboxNotificationRow = ({ notification }) => {
  const [isLoadingChange, setIsLoadingChange] = useState(false);
  const navigate = useNavigate();
  const {
    trackEvent,
    markInAppNotificationAsRead,
    deleteInAppNotification,
    markInAppNotificationAsUnread,
  } = useAPI();

  const colors = useColors();
  const queryClient = useQueryClient();
  const [anchorElMenu, setAnchorElMenu] = useState(null);
  const openMenu = Boolean(anchorElMenu);

  const handleClickMenu = (event) => {
    event.stopPropagation();
    event.preventDefault();

    setAnchorElMenu(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorElMenu(null);
  };

  const notificationTypeIcon = {
    [NotificationType.HIGH_RISK_GAP]: sidebarIcons.ControlsIcon,
    [NotificationType.NEW_SCENARIO_SET]: sidebarIcons.ScenariosIcon,
    [NotificationType.DETECTION]: detectionIcons.DetectionBlack,
  };

  const handleDelete = async () => {
    setIsLoadingChange(true);

    await deleteInAppNotification(notification.timestamp).then(() => {
      trackEvent("User delete notification" + notification.title);
      queryClient.invalidateQueries({
        queryKey: inAppNotificationKeys.all,
      });
      setIsLoadingChange(false);
      handleCloseMenu();
    });
  };
  const handleMarkAsUnread = async () => {
    setIsLoadingChange(true);
    await markInAppNotificationAsUnread(notification.timestamp).then(() => {
      trackEvent("User mark as unread" + notification.title);
      queryClient.invalidateQueries({
        queryKey: inAppNotificationKeys.all,
      });
      setIsLoadingChange(false);
    });
  };

  const markAsRead = async (timestamp) => {
    setIsLoadingChange(true);
    await markInAppNotificationAsRead(timestamp);
    queryClient.invalidateQueries({
      queryKey: inAppNotificationKeys.all,
    });
    setIsLoadingChange(false);
  };

  const handleMarkAsRead = async () => {
    trackEvent(
      "User clicked on mark as read for notification: " + notification.title
    );
    await markAsRead(notification.timestamp);
  };

  const handleNotificationClick = async () => {
    trackEvent("User clicked on notification: " + notification.title);
    !notification?.is_read && (await markAsRead(notification.timestamp));
    navigate(notification?.link);
  };

  const moreActions = [
    {
      title: "Mark as read",
      icon: icons.InboxAllNotificationIcon,
      onclick: handleMarkAsRead,
      disabled: notification.is_read,
    },
    {
      title: "Mark as unread",
      icon: icons.UnreadIcon,
      onclick: handleMarkAsUnread,
      disabled: !notification.is_read,
    },
    {
      title: "Copy URL",
      icon: icons.LinkIcon,
      onclick: () => {
        const fullUrl = `${window.location.origin}${notification.link}`;
        navigator.clipboard.writeText(fullUrl)
          .then(() => {
            trackEvent("User copy URL" + notification.title);
            Toast.success("URL copied to clipboard");
          })
          .catch(() => {
            Toast.error("Failed to copy URL to clipboard");
          });
      },
    },
    {
      title: "Delete",
      icon: icons.DeleteIcon,
      onclick: handleDelete,
    },
  ];

  const NotificationFooter = {
    [NotificationType.HIGH_RISK_GAP]: <FooterGap notification={notification} />,
    [NotificationType.NEW_SCENARIO_SET]: (
      <FooterScenario notification={notification} />
    ),
    [NotificationType.HIGH_RISK_ENTITY]: (
      <FooterEntity notification={notification} />
    ),
    [NotificationType.DETECTION]: <FooterGap notification={notification} />,
  };

  return (
    <Box
      sx={{
        "&:hover": {
          backgroundColor: colors.grey_8percent,
        },
      }}
    >
      <Box
        sx={{
          padding: "16px 16px 0 16px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "16px",
            borderRadius: "8px",
            cursor: "pointer",
            paddingBottom: "16px",
            alignItems: "flex-start",
            width: "340px",
          }}
        >
          <Box sx={{ width: "10px" }}>
            {!notification.is_read && (
              <CircleRoundedIcon
                sx={{
                  color: colors.primary_main,
                  width: "10px",
                }}
              />
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              borderRadius: "8px",
              cursor: "pointer",
              width: "100%",
            }}
            onClick={handleNotificationClick}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box
                sx={{
                  background: colors.primary_8percent,
                  width: "28px",
                  height: "28px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "6px",
                }}
              >
                <SvgToIcon
                  IconLogo={
                    notificationTypeIcon[notification?.notification_type]
                      ? notificationTypeIcon[notification?.notification_type]
                      : severities.OutlineSeverity
                  }
                  style={{
                    width: "18px",
                  }}
                />
              </Box>
              <MoreHorizRoundedIcon
                onClick={handleClickMenu}
                sx={{
                  width: "16px",
                }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "8px",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="overline">{notification?.title}</Typography>
              <Typography
                variant="caption"
                sx={{
                  color: colors.text_secondary,
                }}
              >
                {timePassedSince(notification.date_object.timestamp_utc)}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: "8px",
              }}
            >
              <Typography variant="caption">
                {notification?.description}
              </Typography>
            </Box>
            {NotificationFooter[notification?.notification_type] ? (
              NotificationFooter[notification?.notification_type]
            ) : (
              <FooterEntity notification={notification} />
            )}
          </Box>
        </Box>
        <CustomMenu
          anchorElMenu={anchorElMenu}
          openMenu={openMenu}
          handleCloseMenu={handleCloseMenu}
          moreActions={moreActions}
        />
      </Box>
      <Divider flexItem style={{ marginTop: "10px" }} />
    </Box>
  );
};

export default InboxNotificationRow;
